// Authentication Context - Manages admin auth state and role verification
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db, FUNCTIONS_URL } from '../services/firebase';
import {
    signInWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isModerator, setIsModerator] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Check if user has admin/moderator claims
    const checkAdminClaims = async (firebaseUser) => {
        if (!firebaseUser) {
            setIsAdmin(false);
            setIsModerator(false);
            return false;
        }

        try {
            // Get ID token result to check custom claims
            const tokenResult = await firebaseUser.getIdTokenResult(true);
            const claims = tokenResult.claims;

            const hasAdminClaim = claims.admin === true;
            const hasModeratorClaim = claims.moderator === true;

            // Check hardcoded admin emails first (no Firestore read needed)
            const adminEmails = ['admin@ovuloom.com', 'contact@ovuloom.com', 'gcolemgbe@gmail.com'];
            const isHardcodedAdmin = adminEmails.includes(firebaseUser.email || '');

            // If we already have admin access via claims or hardcoded email, skip Firestore check
            if (hasAdminClaim || isHardcodedAdmin) {
                setIsAdmin(true);
                setIsModerator(true);
                return true;
            }

            if (hasModeratorClaim) {
                setIsAdmin(false);
                setIsModerator(true);
                return true;
            }

            const isDashboardStaff = (data) => {
                if (!data || data.isActive === false) return false;
                const r = String(data.role || '').toLowerCase().replace(/-/g, '_');
                if (['doctor', 'midwife', 'nurse'].includes(r)) return false;
                return ['admin', 'super_admin', 'moderator', 'customer_care', 'customercare'].includes(r);
            };

            try {
                const adminDoc = await getDoc(doc(db, 'admin_users', firebaseUser.uid));
                if (isDashboardStaff(adminDoc.data())) {
                    const r = String(adminDoc.data().role || '').toLowerCase();
                    setIsAdmin(['admin', 'super_admin'].includes(r));
                    setIsModerator(true);
                    return true;
                }

                if (firebaseUser.email) {
                    const emailAdminDoc = await getDoc(doc(db, 'admin_users', firebaseUser.email));
                    if (isDashboardStaff(emailAdminDoc.data())) {
                        const r = String(emailAdminDoc.data().role || '').toLowerCase();
                        setIsAdmin(['admin', 'super_admin'].includes(r));
                        setIsModerator(true);
                        return true;
                    }
                }
            } catch (firestoreErr) {
                console.log('Firestore admin check skipped (expected for non-admins)');
            }

            setIsAdmin(false);
            setIsModerator(false);
            return false;
        } catch (err) {
            console.error('Error checking admin claims:', err);
            setIsAdmin(false);
            setIsModerator(false);
            return false;
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            setLoading(true);
            if (firebaseUser) {
                setUser(firebaseUser);
                await checkAdminClaims(firebaseUser);
            } else {
                setUser(null);
                setIsAdmin(false);
                setIsModerator(false);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Login with email/password
    const loginWithEmail = async (email, password) => {
        setError(null);
        try {
            const result = await signInWithEmailAndPassword(auth, email, password);
            const hasAccess = await checkAdminClaims(result.user);
            if (!hasAccess) {
                await signOut(auth);
                throw new Error('Access denied. Admin or Moderator role required.');
            }
            return result.user;
        } catch (err) {
            const friendlyMessage = getFirebaseErrorMessage(err.code || err.message);
            setError(friendlyMessage);
            throw new Error(friendlyMessage);
        }
    };

    // Login with Google
    const loginWithGoogle = async () => {
        setError(null);
        try {
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            const hasAccess = await checkAdminClaims(result.user);
            if (!hasAccess) {
                await signOut(auth);
                throw new Error('Access denied. Admin or Moderator role required.');
            }
            return result.user;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // Reset password
    const resetPassword = async (email) => {
        setError(null);
        try {
            await sendPasswordResetEmail(auth, email);
        } catch (err) {
            const friendlyMessage = getFirebaseErrorMessage(err.code || err.message);
            setError(friendlyMessage);
            throw new Error(friendlyMessage);
        }
    };

    // Get user-friendly error messages from Firebase error codes
    const getFirebaseErrorMessage = (code) => {
        switch (code) {
            case 'auth/user-not-found':
                return 'No account found with this email address.';
            case 'auth/wrong-password':
            case 'auth/invalid-credential':
                return 'Incorrect email or password. Please try again.';
            case 'auth/invalid-email':
                return 'Please enter a valid email address.';
            case 'auth/user-disabled':
                return 'This account has been disabled.';
            case 'auth/too-many-requests':
                return 'Too many login attempts. Please try again later.';
            case 'auth/network-request-failed':
                return 'Network error. Please check your connection.';
            case 'auth/popup-closed-by-user':
                return 'Sign-in popup was closed. Please try again.';
            default:
                if (typeof code === 'string' && code.includes('Access denied')) return code;
                return 'An error occurred. Please try again.';
        }
    };

    // Logout
    const logout = async () => {
        await signOut(auth);
        setUser(null);
        setIsAdmin(false);
        setIsModerator(false);
    };

    // Get current ID token (for API calls)
    const getIdToken = async () => {
        if (!user) return null;
        return await user.getIdToken();
    };

    // Call setAdminClaims Cloud Function
    const setUserRole = async (targetUserId, role) => {
        const token = await getIdToken();
        if (!token) throw new Error('Not authenticated');

        const response = await fetch(`${FUNCTIONS_URL}/setAdminClaims`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ targetUserId, role })
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || 'Failed to set role');
        }

        return await response.json();
    };

    const value = {
        user,
        isAdmin,
        isModerator,
        loading,
        error,
        loginWithEmail,
        loginWithGoogle,
        resetPassword,
        logout,
        getIdToken,
        setUserRole,
        checkAdminClaims
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
